import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, CheckCircle, Mail, Settings } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API   = process.env.REACT_APP_BACKEND_URL;
const token = () => localStorage.getItem('authToken');

export default function AppSettings() {
  const navigate   = useNavigate();
  const [settings, setSettings] = useState({ lab_dispatch_email: '', sender_email: '' });
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/app-settings/`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(res => setSettings(res.data))
      .catch(() => toast.error('Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await axios.patch(`${API}/api/app-settings/`, {
        lab_dispatch_email: settings.lab_dispatch_email,
        sender_email:       settings.sender_email,
      }, { headers: { Authorization: `Bearer ${token()}` } });
      setSaved(true);
      toast.success('Settings saved');
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50 max-w-md mx-auto">
      {/* Header */}
      <div className="bg-emerald-700 text-white px-4 py-3 flex items-center gap-3 shrink-0">
        <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-emerald-600">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <p className="text-xs text-emerald-200 font-medium">Admin</p>
          <p className="text-base font-bold">App Settings</p>
        </div>
        <Settings className="w-5 h-5 text-emerald-300" />
      </div>

      <div className="flex-1 overflow-y-auto px-4 pt-4">
        {loading ? (
          <p className="text-center py-12 text-gray-400 text-sm">Loading…</p>
        ) : (
          <>
            {/* Lab Dispatch Email */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-4">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
                <Mail className="w-4 h-4 text-emerald-600" />
                <p className="text-sm font-bold text-gray-700">Lab Dispatch Email</p>
              </div>
              <div className="px-4 py-4 space-y-4">
                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase block mb-1">
                    Recipient Email
                  </label>
                  <input
                    type="email"
                    value={settings.lab_dispatch_email || ''}
                    onChange={e => setSettings(s => ({ ...s, lab_dispatch_email: e.target.value }))}
                    placeholder="pranay.hallmark@gmail.com"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-400"
                    data-testid="lab-dispatch-email-input"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">
                    PDF + Excel packing list will be emailed here on every batch dispatch
                  </p>
                </div>

                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase block mb-1">
                    Sender Email
                  </label>
                  <input
                    type="email"
                    value={settings.sender_email || ''}
                    onChange={e => setSettings(s => ({ ...s, sender_email: e.target.value }))}
                    placeholder="onboarding@resend.dev"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-400"
                    data-testid="sender-email-input"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">
                    Use <span className="font-mono">onboarding@resend.dev</span> for testing, or a verified domain address for production (e.g. <span className="font-mono">noreply@yourdomain.com</span>)
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full py-3 bg-emerald-600 text-white font-bold rounded-2xl disabled:opacity-50 active:bg-emerald-700 flex items-center justify-center gap-2"
              data-testid="save-settings-btn"
            >
              {saved ? (
                <><CheckCircle className="w-4 h-4" /> Saved</>
              ) : saving ? (
                'Saving…'
              ) : (
                <><Save className="w-4 h-4" /> Save Settings</>
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
